import { useState, useCallback } from 'react';
import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  type EdgeProps,
  type Edge,
} from '@xyflow/react';
import { makeStyles, tokens, mergeClasses } from '@fluentui/react-components';
import { AddFilled, BotFilled } from '@fluentui/react-icons';
import { EdgePillButton } from './EdgePillButton';

export type AddButtonEdgeData = {
  onAddClick?: (sourceId: string, targetId: string) => void;
  onAddAgentClick?: (sourceId: string, targetId: string) => void;
};

export type AddButtonEdgeType = Edge<AddButtonEdgeData, 'addButton'>;

const useStyles = makeStyles({
  edgeButtonWrapper: {
    position: 'absolute',
    pointerEvents: 'all',
    // Make the hit area larger for easier hover detection
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
  },
  edgeButtonWrapperDual: {
    // Two 24px buttons + 6px gap + breathing room for shadows / hit area
    width: '68px',
  },
  interactionPath: {
    fill: 'none',
    stroke: 'transparent',
    strokeWidth: 20,
    cursor: 'pointer',
  },
  addIcon: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
  },
  agentIcon: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorBrandForeground1,
  },
});

/**
 * Custom edge with an "Add action" button (and optional "Add agent" button)
 * at the midpoint. Both buttons fade in on edge hover.
 */
export const AddButtonEdge = ({
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style,
  markerEnd,
  source,
  target,
  data,
}: EdgeProps<AddButtonEdgeType>) => {
  const styles = useStyles();
  const [isHovered, setIsHovered] = useState(false);
  const showAgentVariant = !!data?.onAddAgentClick;

  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);

  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const handleAddAction = useCallback(() => {
    data?.onAddClick?.(source, target);
  }, [data, source, target]);

  const handleAddAgent = useCallback(() => {
    data?.onAddAgentClick?.(source, target);
  }, [data, source, target]);

  const edgeStyle: React.CSSProperties = {
    ...style,
    strokeWidth: 2,
    stroke: tokens.colorNeutralStroke1,
  };

  return (
    <>
      <BaseEdge
        path={edgePath}
        {...(markerEnd ? { markerEnd } : {})}
        style={edgeStyle}
      />
      {/* Invisible interaction path widens the hover hit area across the edge */}
      <path
        d={edgePath}
        className={styles.interactionPath}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      />
      <EdgeLabelRenderer>
        <div
          className={mergeClasses(
            styles.edgeButtonWrapper,
            showAgentVariant && styles.edgeButtonWrapperDual,
          )}
          style={{
            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <EdgePillButton
            icon={<AddFilled className={styles.addIcon} />}
            tooltip="Add an action"
            ariaLabel="Add an action between these nodes"
            isRevealed={isHovered}
            onActivate={handleAddAction}
          />
          {showAgentVariant && (
            <EdgePillButton
              icon={<BotFilled className={styles.agentIcon} />}
              tooltip="Add an agent"
              ariaLabel="Add an agent between these nodes"
              isRevealed={isHovered}
              onActivate={handleAddAgent}
            />
          )}
        </div>
      </EdgeLabelRenderer>
    </>
  );
};

export default AddButtonEdge;
